
import React from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Edit, Trash2, Settings, Play, Calendar, Clock, Users, BookOpen } from 'lucide-react';
import { Program } from '@/types/program';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

interface ProgramTableProps {
  programs: Program[];
  loading: boolean;
  onEdit: (program: Program) => void;
  onDelete: (programId: string) => void;
  onManageActivities: (program: Program) => void;
  onRunPractice: (program: Program) => void;
  onManagePlayers: (program: Program) => void;
  onManageQuizzes: (program: Program) => void;
}

export const ProgramTable = ({ 
  programs, 
  loading, 
  onEdit, 
  onDelete, 
  onManageActivities, 
  onRunPractice, 
  onManagePlayers,
  onManageQuizzes 
}: ProgramTableProps) => {
  // Query to get activity durations for all programs
  const { data: programDurations = {} } = useQuery({
    queryKey: ['program-durations', programs.map(p => p.id)],
    queryFn: async () => {
      if (programs.length === 0) return {};
      
      const { data, error } = await supabase
        .from('activities')
        .select('program_id, duration')
        .in('program_id', programs.map(p => p.id));

      if (error) throw error;

      // Calculate total duration for each program
      const durations: Record<string, number> = {};
      data?.forEach(activity => {
        durations[activity.program_id] = (durations[activity.program_id] || 0) + activity.duration;
      });

      return durations;
    },
    enabled: programs.length > 0,
  });

  if (loading) {
    return <div className="text-center py-4">Loading programs...</div>;
  }

  if (programs.length === 0) {
    return (
      <div className="text-center py-8">
        <Calendar className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
        <h3 className="text-lg font-semibold mb-2">No Programs Found</h3>
        <p className="text-muted-foreground">
          Create your first training program to get started.
        </p>
      </div>
    );
  }

  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>Title</TableHead>
          <TableHead>Status</TableHead>
          <TableHead>Duration</TableHead>
          <TableHead className="text-right">Actions</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {programs.map((program) => (
          <TableRow key={program.id}>
            <TableCell className="font-medium">{program.title}</TableCell>
            <TableCell>
              <Badge variant={program.is_active ? "default" : "secondary"}>
                {program.is_active ? 'Active' : 'Inactive'}
              </Badge>
            </TableCell>
            <TableCell>
              <div className="flex items-center gap-1 text-muted-foreground">
                <Clock className="h-3 w-3" />
                {programDurations[program.id] || 0} min
              </div>
            </TableCell>
            <TableCell className="text-right">
              <div className="flex justify-end space-x-1">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => onRunPractice(program)}
                  className="text-green-600 hover:text-green-700"
                >
                  <Play className="h-4 w-4" />
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => onManagePlayers(program)}
                  className="text-blue-600 hover:text-blue-700"
                >
                  <Users className="h-4 w-4" />
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => onManageQuizzes(program)}
                  className="text-purple-600 hover:text-purple-700"
                >
                  <BookOpen className="h-4 w-4" />
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => onManageActivities(program)}
                >
                  <Settings className="h-4 w-4" />
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => onEdit(program)}
                >
                  <Edit className="h-4 w-4" />
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => onDelete(program.id)}
                  className="text-red-600 hover:text-red-700"
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
};
